import React, { useState } from "react";
import {
  FaBox, FaMoneyBillWave, FaEdit, FaCalendarAlt, FaWarehouse,
  FaImages, FaTimes, FaTags, FaCloudUploadAlt,
} from "react-icons/fa";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import FileUpload from "../common/FileUpload";

const S3_BASE = "https://flps2025.s3.ap-south-1.amazonaws.com/";
const resolveImageUrl = (img) => (img?.startsWith("http") ? img : `${S3_BASE}${img}`);

// ─── Small building blocks ───────────────────────────────────────────────────
const Badge = ({ children, color = "#6366f1", bg = "#eef2ff" }) => (
  <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: 600, color, background: bg }}>
    {children}
  </span>
);

// Zoho-style "label ... value" row
const DetailRow = ({ label, value, valueColor = "#111827", bold = true }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-50 last:border-b-0">
    <span style={{ fontSize: "13px", color: "#9ca3af", flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: "13.5px", fontWeight: bold ? 600 : 500, color: valueColor, textAlign: "right" }}>
      {value || value === 0 ? value : "—"}
    </span>
  </div>
);

const SectionCard = ({ title, accentColor, icon, children, headerRight = null }) => (
  <div style={{
    background: "#fff", borderRadius: "12px", border: "1px solid #f0f0f5", overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
  }}>
    <div style={{
      padding: "10px 16px", borderBottom: "1px solid #f3f4f6",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      background: `linear-gradient(135deg, ${accentColor}0d 0%, transparent 100%)`
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ color: accentColor, display: "flex" }}>{icon}</span>
        <h3 style={{ margin: 0, fontSize: "12px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#374151" }}>{title}</h3>
      </div>
      {headerRight}
    </div>
    <div style={{ padding: "14px 16px" }}>
      {children}
    </div>
  </div>
);

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Helper function to get brand name from product_brand_id
const getBrandName = (product) => {
  if (product?.product_brand_id) {
    if (typeof product.product_brand_id === 'object' && product.product_brand_id.name) {
      return product.product_brand_id.name;
    }
    if (typeof product.product_brand_id === 'string') {
      return product.product_brand_id;
    }
  }
  if (product?.product_brand) {
    if (typeof product.product_brand === 'object' && product.product_brand.name) {
      return product.product_brand.name;
    }
    return product.product_brand;
  }
  return null;
};

const getManufacturerName = (product) => {
  if (product?.manufacturer_id) {
    if (typeof product.manufacturer_id === 'object' && product.manufacturer_id.name) {
      return product.manufacturer_id.name;
    }
    if (typeof product.manufacturer_id === 'string') {
      return product.manufacturer_id;
    }
  }
  return null;
};

// Helper function to get variant display with specification and unit
const getVariantDisplay = (product) => {
  const variant = product?.variant;
  if (!variant) return null;

  let display = variant.variant_name || '';
  if (variant.specification) {
    display += ` (${variant.specification}`;
    if (variant.quantity_unit) {
      display += ` ${variant.quantity_unit}`;
    }
    display += `)`;
  }
  return display || null;
};

const ProductOverview = ({ product, onProductUpdated, onEditClick }) => {
  const [savingImages, setSavingImages] = useState(false);

  // ── Direct image management right from the Overview page ──────────────────
  const persistImages = async (newImages) => {
    if (!product?._id) return;
    setSavingImages(true);
    try {
      const response = await httpService.put(
        `/vendorproduct/updateVendorProduct/${product._id}`,
        { images: newImages }
      );
      if (response.status === true || response.code === 200) {
        onProductUpdated({ ...product, images: newImages, ...(response.data || {}) });
        toast.success("Images updated");
      } else {
        toast.error(response.message || "Failed to update images");
      }
    } catch (error) {
      console.error("Error updating images:", error);
      toast.error(error.response?.data?.message || "Failed to update images");
    } finally {
      setSavingImages(false);
    }
  };

  const handleUploadComplete = (urls) => {
    persistImages(urls);
  };

  if (!product) return null;

  const brandName = getBrandName(product);
  const manufacturerName = getManufacturerName(product);
  const images = product.images || [];
  
  // ✅ Get attributes from variant (where they're actually stored)
  const getVariantAttributes = () => {
    const variant = product?.variant;
    if (!variant) return [];
    
    // If variant has attributes array
    if (variant.attributes && Array.isArray(variant.attributes) && variant.attributes.length > 0) {
      return variant.attributes;
    }
    
    return [];
  };
  
  const variantAttributes = getVariantAttributes();
  
  // ✅ Get display name for hero card (full variant name)
  const getDisplayName = () => {
    const variant = product?.variant;
    if (variant && variant.variant_name) {
      return variant.variant_name;
    }
    return product.name || "—";
  };
  
  const displayName = getDisplayName();
  
  const itemTypeLabel = product.variant ? "Variant Item" : "Standalone Item";

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", width: "100%", boxSizing: "border-box" }}>
      {/* ── Main Grid: Details (left, wider) + Images (right) ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: "12px", width: "100%", boxSizing: "border-box", alignItems: "start" }}>

        {/* ── Left column ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>

          {/* Primary Details */}
          <SectionCard title="Primary Details" accentColor="#6366f1" icon={<FaBox size={13} />}>
            {/* ✅ Show full variant name in the details */}
            <DetailRow label="Product Name" value={product.name} />
            
            {/* ✅ Show variant attributes from variant.attributes */}
            {variantAttributes.map((attr) => (
              <DetailRow
                key={attr._id || attr.attribute_name || attr.name}
                label={attr.attribute_name || attr.name}
                value={Array.isArray(attr.value) ? attr.value.join(", ") : String(attr.value)}
                bold={true}
                valueColor="#4b5563"
              />
            ))}
            <DetailRow label="Brand" value={brandName} />
            <DetailRow label="Manufacturer" value={manufacturerName} />
            <DetailRow label="SKU" value={product.sku_number} />
            <DetailRow label="Barcode" value={product.barcode} />
            <DetailRow label="Unit" value={product.variant?.sales_unit || product.unit} />
            <DetailRow
              label="Category"
              value={product.product_category?.map((c) => c.name).join(", ")}
            />
            <DetailRow
              label="Subcategory"
              value={product.product_subcategory?.map((s) => s.name).join(", ")}
            />
            {product.description && (
              <div style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #f3f4f6" }}>
                <p style={{ margin: "0 0 3px", fontSize: "11px", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "#9ca3af" }}>Description</p>
                <p style={{ margin: 0, fontSize: "13.5px", color: "#374151", lineHeight: 1.5 }}>{product.description}</p>
              </div>
            )}
          </SectionCard>

          {/* Pricing & Tax */}
          <SectionCard title="Pricing & Tax" accentColor="#059669" icon={<FaMoneyBillWave size={13} />}>
            <DetailRow label="MRP" value={`₹${product.mrp || 0}`} />
            <DetailRow label="Sale Price" value={`₹${product.effective_sale_price || 0}${product.unit ? ` / ${product.unit}` : ""}`} valueColor="#059669" />
            <DetailRow label="Purchase Price" value={`₹${product.purchase_price || 0}`} />
            <DetailRow label="GST Slab" value={<Badge color="#d97706" bg="#fef3c7">{product.gst_slab || 0}%</Badge>} />
            <DetailRow label="HSN Code" value={product.hsn_code} />
          </SectionCard>

        </div>

        {/* ── Right column: Images + Stock ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>

          {/* Images */}
          <SectionCard
            title="Product Images"
            accentColor="#8b5cf6"
            icon={<FaImages size={13} />}
            headerRight={savingImages ? <span style={{ fontSize: "11px", color: "#9ca3af" }}>Saving…</span> : null}
          >
            <FileUpload
              uploadEndpoint="/upload/fileUpload"
              acceptedTypes={["image/png", "image/jpeg", "image/gif"]}
              multiple
              maxFiles={10}
              moduleName="vendors"
              docType="products/images"
              existingUrls={images}
              onUploadComplete={handleUploadComplete}
            />
          </SectionCard>

          {/* Stock & Tracking */}
          <SectionCard title="Stock & Tracking" accentColor="#0891b2" icon={<FaWarehouse size={13} />}>
            <div style={{
              background: product.quantity_available > 0 ? "#f0fdf4" : "#fef2f2",
              border: `1px solid ${product.quantity_available > 0 ? "#bbf7d0" : "#fecaca"}`,
              borderRadius: "10px", padding: "10px 14px", marginBottom: "10px",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <span style={{
                fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                color: product.quantity_available > 0 ? "#16a34a" : "#dc2626",
              }}>In Stock</span>
              <span style={{
                fontSize: "22px", fontWeight: 800,
                color: product.quantity_available > 0 ? "#16a34a" : "#dc2626",
              }}>{product.quantity_available || 0}</span>
            </div>
            <DetailRow label="Track Inventory" value={product.track_inventory ? "Yes" : "No"} />
            {product.track_inventory && (
              <DetailRow label="Restock Threshold" value={product.restock_alert || "—"} />
            )}
            <DetailRow label="Expiry Tracked" value={product.is_expiry_tracked ? "Yes (FEFO)" : "No (FIFO)"} />
          </SectionCard>

        </div>
      </div>

      {/* Creation Date Card - Full Width */}
      <div style={{ marginTop: "12px", width: "100%" }}>
        <SectionCard title="Product Timeline" accentColor="#8b5cf6" icon={<FaCalendarAlt size={13} />}>
          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
            <div style={{ flex: 1 }}>
              <p style={{ margin: "0 0 3px", fontSize: "11px", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "#9ca3af" }}>Date Added / Created</p>
              <p style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "#111827", display: "flex", alignItems: "center", gap: "6px" }}>
                <FaCalendarAlt size={12} color="#a78bfa" /> {formatDate(product.createdAt)}
              </p>
            </div>
            {product.updatedAt && (
              <div style={{ flex: 1 }}>
                <p style={{ margin: "0 0 3px", fontSize: "11px", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "#9ca3af" }}>Last Updated</p>
                <p style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "#6b7280", display: "flex", alignItems: "center", gap: "6px" }}>
                  <FaTags size={12} color="#a78bfa" /> {formatDate(product.updatedAt)}
                </p>
              </div>
            )}
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default ProductOverview;